import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MultiSelectAutocomplete } from '../components/MultiSelectAutocomplete';
import { DateRangePicker } from '../components/DateRangePicker';
import { EmployeeMultiSelect } from '../components/EmployeeMultiSelect';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────────────────────────
// Visual tokens (aligned with rest of the app)
// ──────────────────────────────────────────────────────────────────────────────
const C = {
  bgPage:      '#0f172a',
  bgPanel:     '#1e293b',
  bgPanel2:    '#273449',
  borderSoft:  '#334155',
  textMain:    '#f1f5f9',
  textMuted:   '#94a3b8',
  textDim:     '#64748b',
  danger:      '#ef4444',
  dangerBg:    'rgba(239, 68, 68, 0.12)',
  warning:     '#f59e0b',
  warningBg:   'rgba(245, 158, 11, 0.12)',
  info:        '#60a5fa',
  infoBg:      'rgba(96, 165, 250, 0.12)',
  success:     '#22c55e',
  successBg:   'rgba(34, 197, 94, 0.12)',
  purple:      '#a78bfa',
  purpleBg:    'rgba(167, 139, 250, 0.12)',
  teal:        '#2dd4bf',
  tealBg:      'rgba(45, 212, 191, 0.12)',
  pink:        '#f472b6',
  pinkBg:      'rgba(244, 114, 182, 0.12)',
};

const CATEGORIES = [
  {
    key:         'jornadas_excesivas',
    title:       'Jornadas excesivas (>12h)',
    description: 'Empleados con >12h trabajadas en un solo día. Posible riesgo laboral o fichaje incorrecto.',
    color:       C.danger,
    bg:          C.dangerBg,
    icon:        '⚠️',
    priority:    'alta',
    columns: [
      { k: 'nombre_completo', label: 'Empleado' },
      { k: 'svc_date',        label: 'Fecha' },
      { k: 'day_hours',       label: 'Horas',    fmt: (v) => `${v?.toFixed(2)} h` },
      { k: 'centro_coste',    label: 'Centro' },
      { k: 'first_ts_day',    label: 'Entrada',  fmt: (v) => v?.slice(11, 16) ?? '—' },
      { k: 'last_ts_day',     label: 'Salida',   fmt: (v) => v?.slice(11, 16) ?? '—' },
    ],
  },
  {
    key:         'jornadas_cortas',
    title:       'Jornadas sospechosamente cortas (<2h)',
    description: 'Fichajes de menos de 2 horas sin auto-cierre. Probable error de fichaje.',
    color:       C.warning,
    bg:          C.warningBg,
    icon:        '⏱️',
    priority:    'media',
    columns: [
      { k: 'nombre_completo', label: 'Empleado' },
      { k: 'svc_date',        label: 'Fecha' },
      { k: 'day_hours',       label: 'Horas',    fmt: (v) => `${v?.toFixed(2)} h` },
      { k: 'centro_coste',    label: 'Centro' },
      { k: 'first_ts_day',    label: 'Entrada',  fmt: (v) => v?.slice(11, 16) ?? '—' },
      { k: 'last_ts_day',     label: 'Salida',   fmt: (v) => v?.slice(11, 16) ?? '—' },
    ],
  },
  {
    key:         'sin_centro',
    title:       'Empleados sin centro asignado',
    description: 'Fichan en terminal pero no aparecen en el CSV de RRHH. Requiere verificar roster.',
    color:       C.purple,
    bg:          C.purpleBg,
    icon:        '👤',
    priority:    'alta',
    adminOnly:   true,
    columns: [
      { k: '_id',         label: 'ID Empleado' },
      { k: 'nombre_completo', label: 'Nombre' },
      { k: 'dias',        label: 'Días',     fmt: (v) => `${v} días` },
      { k: 'total_horas', label: 'Horas',    fmt: (v) => `${v?.toFixed(1)} h` },
      { k: 'last_ts',     label: 'Último fichaje', fmt: (v) => v?.slice(0, 16).replace('T', ' ') ?? '—' },
    ],
  },
  {
    key:         'auto_cierre_reiterado',
    title:       'Auto-cierre reiterado (3+ días)',
    description: 'Empleados que no marcan salida en 3 o más días. Revisar con el responsable.',
    color:       C.info,
    bg:          C.infoBg,
    icon:        '🔄',
    priority:    'media',
    columns: [
      { k: '_id',        label: 'ID Empleado' },
      { k: 'nombre_completo', label: 'Nombre' },
      { k: 'centro',     label: 'Centro' },
      { k: 'dias',       label: 'Días auto-cerrados', fmt: (v) => `${v} días` },
      { k: 'last_date',  label: 'Último' },
    ],
  },
  {
    key:         'horas_extra_probables',
    title:       'Horas extra probables (9-12h)',
    description: 'Jornadas entre 9 y 12 h en día laborable. Revisar compensación.',
    color:       C.teal,
    bg:          C.tealBg,
    icon:        '💪',
    priority:    'media',
    columns: [
      { k: 'nombre_completo', label: 'Empleado' },
      { k: 'svc_date',        label: 'Fecha' },
      { k: 'day_hours',       label: 'Horas', fmt: (v) => `${v?.toFixed(2)} h` },
      { k: 'centro_coste',    label: 'Centro' },
    ],
  },
  {
    key:         'fichajes_fin_semana',
    title:       'Fichajes en fin de semana',
    description: 'Trabajos en sábado o domingo. Verificar que están justificados por contrato.',
    color:       C.pink,
    bg:          C.pinkBg,
    icon:        '🏝️',
    priority:    'baja',
    columns: [
      { k: 'nombre_completo', label: 'Empleado' },
      { k: 'svc_date',        label: 'Fecha' },
      { k: 'day_hours',       label: 'Horas', fmt: (v) => `${v?.toFixed(2)} h` },
      { k: 'shift_name',      label: 'Turno' },
      { k: 'centro_coste',    label: 'Centro' },
    ],
  },
  {
    key:         'turno_inusual',
    title:       'Alternancia de turnos (Mañana + Noche)',
    description: 'Empleados con turnos mañana y noche en la misma semana. Posible sobrecarga.',
    color:       C.warning,
    bg:          C.warningBg,
    icon:        '🌗',
    priority:    'baja',
    columns: [
      { k: '_id',            label: 'ID Empleado' },
      { k: 'nombre_completo', label: 'Nombre' },
      { k: 'centro',         label: 'Centro' },
      { k: 'shifts',         label: 'Turnos', fmt: (v) => Array.isArray(v) ? v.join(', ') : v },
      { k: 'dias',           label: 'Días', fmt: (v) => `${v} días` },
    ],
  },
];

const fmtCell = (row, col) => {
  let v = row[col.k];
  if (col.k === 'nombre_completo') {
    v = `${row.nombre || ''} ${row.apellidos || ''}`.trim() || row.id_employee || row._id || '—';
  }
  if (col.fmt) return col.fmt(v);
  if (v === null || v === undefined || v === '') return '—';
  return v;
};

const PRIORITY_BADGE = {
  alta:   { label: 'ALTA',  bg: C.dangerBg,  color: C.danger },
  media:  { label: 'MEDIA', bg: C.warningBg, color: C.warning },
  baja:   { label: 'BAJA',  bg: C.infoBg,    color: C.info },
};

// ──────────────────────────────────────────────────────────────────────────────
// Components
// ──────────────────────────────────────────────────────────────────────────────
function DateRangeControls({ startDate, endDate, onChangeRange, onRefresh, loading }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={onChangeRange}
        presets={true}
        testIdPrefix="anomaly"
      />
      <button
        style={{
          background: '#60a5fa',
          border: '1px solid #60a5fa',
          color: '#0f172a',
          padding: '8px 14px', borderRadius: 6, fontSize: 13,
          cursor: 'pointer', fontWeight: 600, marginLeft: 'auto',
          opacity: loading ? 0.5 : 1,
        }}
        onClick={onRefresh}
        disabled={loading}
        data-testid="refresh-anomalies"
      >
        {loading ? 'Cargando…' : '↻ Recalcular'}
      </button>
    </div>
  );
}

function SummaryCards({ summary }) {
  const totalAnomalies = Object.values(summary || {}).reduce((a, b) => a + b, 0);
  const cards = [
    { k: 'total',  label: 'Total anomalías', value: totalAnomalies,                   color: C.danger  },
    { k: 'alta',   label: 'Prioridad alta',  value: (summary?.jornadas_excesivas || 0) + (summary?.sin_centro || 0), color: C.danger },
    { k: 'media',  label: 'Prioridad media', value: (summary?.jornadas_cortas || 0) + (summary?.auto_cierre_reiterado || 0) + (summary?.horas_extra_probables || 0), color: C.warning },
    { k: 'baja',   label: 'Prioridad baja',  value: (summary?.fichajes_fin_semana || 0) + (summary?.turno_inusual || 0), color: C.info },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
      {cards.map(c => (
        <div key={c.k} style={{
          background: C.bgPanel, border: `1px solid ${C.borderSoft}`, borderRadius: 8,
          padding: 16, borderLeft: `4px solid ${c.color}`,
        }} data-testid={`summary-card-${c.k}`}>
          <div style={{ fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: c.color, marginTop: 4 }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function CategorySection({ cat, rows, loading }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 5);
  const badge   = PRIORITY_BADGE[cat.priority] || PRIORITY_BADGE.media;

  return (
    <div style={{
      background: C.bgPanel, border: `1px solid ${C.borderSoft}`, borderRadius: 8,
      borderLeft: `4px solid ${cat.color}`, overflow: 'hidden',
    }} data-testid={`category-${cat.key}`}>
      <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 28 }}>{cat.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: C.textMain }}>{cat.title}</h3>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: badge.bg, color: badge.color, letterSpacing: 0.5,
            }}>{badge.label}</span>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 10,
              background: cat.bg, color: cat.color, fontWeight: 600,
            }}>
              {rows.length} {rows.length === 1 ? 'caso' : 'casos'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>{cat.description}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 16, color: C.textMuted, fontSize: 13 }}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '24px 16px', color: C.success, fontSize: 14, textAlign: 'center',
                      background: C.successBg, borderTop: `1px solid ${C.borderSoft}` }}>
          ✓ Sin incidencias en este periodo
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', borderTop: `1px solid ${C.borderSoft}` }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bgPanel2 }}>
                  {cat.columns.map(c => (
                    <th key={c.k} style={{
                      padding: '8px 12px', textAlign: 'left', fontSize: 11,
                      textTransform: 'uppercase', letterSpacing: 0.5, color: C.textMuted, fontWeight: 600,
                    }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                    {cat.columns.map(c => (
                      <td key={c.k} style={{ padding: '8px 12px', color: C.textMain }}>
                        {fmtCell(row, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 5 && (
            <div style={{ borderTop: `1px solid ${C.borderSoft}`, padding: 8, textAlign: 'center' }}>
              <button onClick={() => setExpanded(!expanded)} data-testid={`expand-${cat.key}`} style={{
                background: 'transparent', border: 'none', color: C.info, cursor: 'pointer',
                fontSize: 13, padding: '6px 12px',
              }}>
                {expanded ? 'Mostrar menos' : `Ver los ${rows.length} registros`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────
export default function AnomaliasPage() {
  const { token, user } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [facets, setFacets]   = useState({ centros: [], turnos: [], estados: [] });
  const [selCentros,   setSelCentros]   = useState([]);
  const [selTurnos,    setSelTurnos]    = useState([]);
  const [selEstados,   setSelEstados]   = useState([]);
  const [selEmpleados, setSelEmpleados] = useState([]); // [{id, label, centro}]

  // Default window: last 7 days ending yesterday
  const y  = new Date(); y.setDate(y.getDate() - 1);
  const s7 = new Date(y); s7.setDate(s7.getDate() - 6);
  const [startDate, setStartDate] = useState(s7.toISOString().slice(0, 10));
  const [endDate,   setEndDate]   = useState(y.toISOString().slice(0, 10));

  // Load facets once
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND}/api/kpis/facets`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null).then(d => d && setFacets(d)).catch(() => {});
  }, [token]);

  // Build query params shared by all endpoints that care about filters
  const buildParams = () => {
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (selCentros.length)   p.set('centros',   selCentros.join(','));
    if (selTurnos.length)    p.set('turnos',    selTurnos.join(','));
    if (selEstados.length)   p.set('estados',   selEstados.join(','));
    if (selEmpleados.length) p.set('empleados', selEmpleados.map(e => e.id).join(','));
    return p;
  };

  const fetchAnomalies = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BACKEND}/api/anomalies?${buildParams()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAnomalies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selCentros, selTurnos, selEstados, selEmpleados, token]);

  const filtersCount = selCentros.length + selTurnos.length + selEstados.length + selEmpleados.length;

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: C.textMain }} data-testid="anomalies-title">
          Anomalías y alertas
        </h1>
        <p style={{ margin: '4px 0 0', color: C.textMuted, fontSize: 14 }}>
          Detección automática de incidencias en fichajes. {data?.window?.days ? `Ventana: ${data.window.days} días.` : ''}
        </p>
      </div>

      <DateRangeControls
        startDate={startDate} endDate={endDate}
        onChangeRange={({ startDate: s, endDate: e }) => {
          if (s) setStartDate(s);
          if (e) setEndDate(e);
        }}
        onRefresh={fetchAnomalies} loading={loading}
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <MultiSelectAutocomplete
          options={facets.centros} value={selCentros} onChange={setSelCentros}
          label="Centro" placeholder="Buscar centro…" testId="filter-centros"
        />
        <MultiSelectAutocomplete
          options={facets.turnos} value={selTurnos} onChange={setSelTurnos}
          label="Turno" placeholder="Buscar turno…" testId="filter-turnos"
        />
        <MultiSelectAutocomplete
          options={facets.estados} value={selEstados} onChange={setSelEstados}
          label="Estado" placeholder="Buscar estado…" testId="filter-estados"
        />
        <EmployeeMultiSelect
          value={selEmpleados} onChange={setSelEmpleados}
          token={token} backendUrl={BACKEND}
          testId="filter-empleados"
        />
        {filtersCount > 0 && (
          <button
            onClick={() => { setSelCentros([]); setSelTurnos([]); setSelEstados([]); setSelEmpleados([]); }}
            style={{
              padding: '8px 12px', background: 'transparent',
              border: `1px solid ${C.borderSoft}`, color: C.textMuted,
              borderRadius: 6, cursor: 'pointer', fontSize: 13,
            }}
            data-testid="clear-all-filters"
          >
            Quitar filtros ({filtersCount})
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: C.dangerBg, border: `1px solid ${C.danger}`, color: C.danger,
          padding: 12, borderRadius: 6, fontSize: 13,
        }}>
          Error cargando anomalías: {error}
        </div>
      )}

      {data && <SummaryCards summary={data.summary} />}

      <div style={{ display: 'grid', gap: 16 }}>
        {CATEGORIES
          .filter(cat => !cat.adminOnly || user?.role === 'admin')
          .map(cat => (
            <CategorySection
              key={cat.key}
              cat={cat}
              rows={data?.[cat.key] || []}
              loading={loading}
            />
          ))}
      </div>
    </div>
  );
}
